import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Select from "react-select";

function Registration() {
  const navigate = useNavigate();

  // Theme colors
  const accentColor = "rgb(219, 68, 55)";
  const backgroundColor = "#ffffff";
  const textColor = "#000000";
  const textSecondary = "#666666";
  const borderColor = "#e0e0e0";

  // Form state
  const [formData, setFormData] = useState({
    company_id: "2",
    dealer_mobile: "",
    dealer_firm_name: "",
    dealer_state_id: "",
    dealer_city_id: "",
    dealer_sub_city_id: "",
  });

  const [otp, setOtp] = useState("");
  const [hide_show, setHide_show] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Dropdown data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [subCities, setSubCities] = useState([]);

  // Dropdown loading states
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingSubCities, setLoadingSubCities] = useState(false);

  let server_url = localStorage.getItem("server_url");

  const getDeviceId = () => {
    let deviceId = localStorage.getItem("dealer_device_id");
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem("dealer_device_id", deviceId);
    }
    return deviceId;
  };

  const dealer_device_id = getDeviceId();

  // Fetch states on component mount
  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Fetch states
  const fetchStates = async () => {
    setLoadingStates(true);
    const formData = new FormData();
    formData.append("company_id", "2");

    try {
      const res = await axios.post(`${server_url}state.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status === "1" && res.data.result) {
        const formattedStates = res.data.result.map((state) => ({
          value: state.state_id,
          label: state.state_name,
        }));
        setStates(formattedStates);
      } else {
        toast.error("Failed to load states");
      }
    } catch (error) {
      toast.error("Failed to load states. Please check your connection.");
    } finally {
      setLoadingStates(false);
    }
  };

  // Fetch cities when state is selected
  const fetchCities = async (stateId) => {
    setLoadingCities(true);
    setCities([]);
    setSubCities([]);
    setFormData((prev) => ({
      ...prev,
      dealer_city_id: "",
      dealer_sub_city_id: "",
    }));

    const formData = new FormData();
    formData.append("company_id", "2");
    formData.append("state_id", stateId);

    try {
      const res = await axios.post(`${server_url}city.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status === "1" && res.data.result) {
        const formattedCities = res.data.result.map((city) => ({
          value: city.city_id,
          label: city.city_name,
        }));
        setCities(formattedCities);
      } else {
        toast.error("Failed to load cities");
      }
    } catch (error) {
      toast.error("Failed to load cities. Please try again.");
    } finally {
      setLoadingCities(false);
    }
  };

  // Fetch sub-cities when city is selected
  const fetchSubCities = async (cityId) => {
    setLoadingSubCities(true);
    setSubCities([]);
    setFormData((prev) => ({ ...prev, dealer_sub_city_id: "" }));

    const formData = new FormData();
    formData.append("company_id", "2");
    formData.append("city_id", cityId);

    try {
      const res = await axios.post(`${server_url}sub_city.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status === "1" && res.data.result) {
        const formattedSubCities = res.data.result.map((subCity) => ({
          value: subCity.sub_city_id,
          label: subCity.sub_city_name,
        }));
        setSubCities(formattedSubCities);
      } else {
        toast.error("Failed to load sub-cities");
      }
    } catch (error) {
      toast.error("Failed to load sub-cities. Please try again.");
    } finally {
      setLoadingSubCities(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle dropdown changes
  const handleStateChange = (selectedOption) => {
    const stateId = selectedOption ? selectedOption.value : "";
    const stateName = selectedOption ? selectedOption.label : "";

    setFormData((prev) => ({
      ...prev,
      dealer_state_id: stateId,
      dealer_state_name: stateName,
      dealer_city_id: "",
      dealer_city_name: "",
      dealer_sub_city_id: "",
      dealer_sub_city_name: "",
    }));

    if (selectedOption) {
      fetchCities(stateId);
    } else {
      setCities([]);
      setSubCities([]);
    }

    // Clear ALL location validation errors
    setValidationErrors((prev) => ({
      ...prev,
      dealer_state_id: "",
      dealer_city_id: "",
      dealer_sub_city_id: "",
    }));
  };

  const handleCityChange = (selectedOption) => {
    const cityId = selectedOption ? selectedOption.value : "";
    const cityName = selectedOption ? selectedOption.label : "";

    setFormData((prev) => ({
      ...prev,
      dealer_city_id: cityId,
      dealer_city_name: cityName,
      dealer_sub_city_id: "",
      dealer_sub_city_name: "",
    }));

    if (selectedOption) {
      fetchSubCities(cityId);
    } else {
      setSubCities([]);
    }

    // Clear validation errors
    setValidationErrors((prev) => ({
      ...prev,
      dealer_city_id: "",
    }));
  };

  const handleSubCityChange = (selectedOption) => {
    const subCityId = selectedOption ? selectedOption.value : "";
    const subCityName = selectedOption ? selectedOption.label : "";

    setFormData((prev) => ({
      ...prev,
      dealer_sub_city_id: subCityId,
      dealer_sub_city_name: subCityName,
    }));

    // Clear validation errors
    setValidationErrors((prev) => ({
      ...prev,
      dealer_sub_city_id: "",
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    const {
      dealer_mobile,
      dealer_firm_name,
      dealer_state_id,
      dealer_city_id,
      dealer_sub_city_id,
    } = formData;

    // Mobile validation
    if (!dealer_mobile) {
      errors.dealer_mobile = "Mobile number is required";
    } else if (dealer_mobile.length !== 10) {
      errors.dealer_mobile = "Please enter a valid 10-digit mobile number";
    }

    // Firm name validation
    if (!dealer_firm_name.trim()) {
      errors.dealer_firm_name = "Firm name is required";
    }

    // State validation
    if (!dealer_state_id) {
      errors.dealer_state_id = "Please select a state";
    }

    // Only validate city if state is selected
    if (dealer_state_id && !dealer_city_id) {
      errors.dealer_city_id = "Please select a city";
    }

    // Only validate sub-city if city is selected
    if (dealer_city_id && !dealer_sub_city_id) {
      errors.dealer_sub_city_id = "Please select a sub-city";
    }

    setValidationErrors(errors);

    // Return true if no errors
    return Object.keys(errors).length === 0;
  };

  // Send OTP for registration
  async function sendRegistrationOTP() {
    // Validate form
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsLoading(true);

    const formDataObj = new FormData();
    formDataObj.append("company_id", formData.company_id);
    formDataObj.append("dealer_mobile", formData.dealer_mobile);
    formDataObj.append("dealer_firm_name", formData.dealer_firm_name);
    formDataObj.append("dealer_state_id", formData.dealer_state_id);
    formDataObj.append("dealer_city_id", formData.dealer_city_id);
    formDataObj.append("dealer_sub_city_id", formData.dealer_sub_city_id);

    try {
      const res = await axios.post(
        `${server_url}dealer_register_with_otp.php`,
        formDataObj,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.data.status === "0") {
        toast.error(res.data.error || "Registration failed");
      } else if (res.data.status === "1") {
        toast.success(res.data.result?.error || "OTP sent successfully!");
        setHide_show(2);
      } else {
        toast.error("Please contact admin.");
      }
    } catch (e) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  // Resend OTP
  async function resendOTP() {
    setIsLoading(true);

    const formDataObj = new FormData();
    formDataObj.append("company_id", formData.company_id);
    formDataObj.append("dealer_mobile", formData.dealer_mobile);
    formDataObj.append("dealer_android_token", "");
    formDataObj.append("dealer_ios_token", "");

    try {
      const res = await axios.post(
        `${server_url}dealer_resend_otp.php`,
        formDataObj,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.data.status === "1") {
        toast.success("OTP resent successfully!");
      } else {
        toast.error(res.data.error || "Failed to resend OTP");
      }
    } catch (e) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  // Verify OTP and complete registration
  async function verifyRegistrationOTP() {
    if (!otp || otp.length !== 4) {
      toast.error("Please enter 4-digit OTP");
      return;
    }

    setIsLoading(true);

    const formDataObj = new FormData();
    formDataObj.append("company_id", formData.company_id);
    formDataObj.append("dealer_mobile", formData.dealer_mobile);
    formDataObj.append("dealer_otp", otp);
    formDataObj.append("dealer_android_token", "");
    formDataObj.append("dealer_ios_token", "");
    formDataObj.append("dealer_device_name", "Web");
    formDataObj.append("dealer_device_id", dealer_device_id);

    try {
      const res = await axios.post(
        `${server_url}dealer_register_otp_verify.php`,
        formDataObj,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.data.status === "0") {
        toast.error(res.data.error || "Verification failed");
      } else if (res.data.status === "1") {
        const dealerInfo = res.data.result[0];

        // Store dealer information
        localStorage.setItem("token", dealer_device_id);
        localStorage.setItem("LoginNumber", formData.dealer_mobile);
        localStorage.setItem("dealer_id", dealerInfo.dealer_id);
        localStorage.setItem("dealer_firm_name", dealerInfo.dealer_firm_name);

        toast.success("Registration successful! Welcome to our platform.");
        navigate("/");
      } else {
        toast.error("Please contact admin.");
      }
    } catch (e) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  // Custom styles for react-select with better readability and z-index
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: backgroundColor,
      border: `1px solid ${borderColor}`,
      borderRadius: "12px",
      minHeight: "50px",
      paddingLeft: "40px",
      boxShadow: state.isFocused ? `0 0 0 3px rgba(219, 68, 55, 0.2)` : "none",
      borderColor: state.isFocused
        ? accentColor
        : validationErrors.dealer_state_id
          ? accentColor
          : borderColor,
      "&:hover": {
        borderColor: validationErrors.dealer_state_id ? accentColor : accentColor,
      },
      zIndex: 999,
      position: "relative",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: textSecondary,
      fontSize: "15px",
      fontWeight: "400",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: textColor,
      fontSize: "16px",
      fontWeight: "500",
    }),
    input: (provided) => ({
      ...provided,
      color: textColor,
      fontSize: "16px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: textSecondary,
      paddingRight: "12px",
      "&:hover": {
        color: accentColor,
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: backgroundColor,
      borderRadius: "12px",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
      zIndex: 9999,
      marginTop: "4px",
      position: "absolute",
      width: "100%",
      border: `1px solid ${borderColor}`,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "8px",
      maxHeight: "200px",
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? accentColor
        : state.isFocused
          ? `rgba(219, 68, 55, 0.1)`
          : backgroundColor,
      color: state.isSelected ? "#ffffff" : textColor,
      fontSize: "15px",
      padding: "12px 16px",
      borderRadius: "8px",
      margin: "2px 0",
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: `rgba(219, 68, 55, 0.2)`,
      },
      "&:active": {
        backgroundColor: accentColor,
        color: "#ffffff",
      },
      zIndex: 9999,
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: textSecondary,
      fontSize: "14px",
      padding: "12px",
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: textSecondary,
      fontSize: "14px",
      padding: "12px",
    }),
    container: (provided) => ({
      ...provided,
      zIndex: 999,
      position: "relative",
    }),
    valueContainer: (provided) => ({
      ...provided,
      zIndex: 999,
    }),
  };

  return (
    <>
      <div className="login-container">
        {/* Registration Card */}
        <div className="login-card">
          <div className="logo-section">
            <div className="logo-circle">
              <i className="fa fa-user-plus"></i>
            </div>
            <h2>Create Account</h2>
            <p>Register as a dealer</p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (hide_show === 1) {
                sendRegistrationOTP();
              } else {
                verifyRegistrationOTP();
              }
            }}
            className="login-form"
          >
            {/* Mobile Number */}
            <div className="input-group">
              <div className="input-wrapper">
                <i className="fa fa-phone input-icon"></i>
                <input
                  type="text"
                  inputMode="numeric"
                  name="dealer_mobile"
                  value={formData.dealer_mobile}
                  onChange={handleInputChange}
                  placeholder=" "
                  readOnly={hide_show === 2}
                  className={`input-field ${validationErrors.dealer_mobile ? "error" : ""}`}
                  id="mobile"
                />
                <label htmlFor="mobile" className="input-label">
                  Mobile Number *
                </label>
              </div>
              {(validationErrors.dealer_mobile ||
                (formData.dealer_mobile.length > 0 &&
                  formData.dealer_mobile.length !== 10)) && (
                <small className="error-text">
                  {validationErrors.dealer_mobile || "Enter 10 digits"}
                </small>
              )}
            </div>

            {/* Firm Name */}
            <div className="input-group">
              <div className="input-wrapper">
                <i className="fa fa-building input-icon"></i>
                <input
                  type="text"
                  name="dealer_firm_name"
                  value={formData.dealer_firm_name}
                  onChange={handleInputChange}
                  placeholder=" "
                  readOnly={hide_show === 2}
                  className={`input-field ${validationErrors.dealer_firm_name ? "error" : ""}`}
                  id="firmName"
                />
                <label htmlFor="firmName" className="input-label">
                  Firm Name *
                </label>
              </div>
              {validationErrors.dealer_firm_name && (
                <small className="error-text">
                  {validationErrors.dealer_firm_name}
                </small>
              )}
            </div>

            {/* State Dropdown */}
            <div className="input-group dropdown-container">
              <div className="input-wrapper dropdown-wrapper">
                <i className="fa fa-map-marker-alt input-icon dropdown-icon"></i>
                <Select
                  options={states}
                  value={
                    states.find(
                      (state) => state.value === formData.dealer_state_id,
                    ) || null
                  }
                  onChange={handleStateChange}
                  placeholder={
                    loadingStates ? "Loading states..." : "Select State *"
                  }
                  isLoading={loadingStates}
                  isDisabled={hide_show === 2 || loadingStates}
                  isSearchable={true}
                  styles={customStyles}
                  classNamePrefix="react-select"
                  className={`react-select-container ${validationErrors.dealer_state_id ? "error" : ""}`}
                  noOptionsMessage={() => "No states available"}
                  loadingMessage={() => "Loading states..."}
                  aria-label="Select State"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              {validationErrors.dealer_state_id && (
                <small className="error-text">
                  {validationErrors.dealer_state_id}
                </small>
              )}
            </div>

            {/* City Dropdown */}
            <div className="input-group dropdown-container">
              <div className="input-wrapper dropdown-wrapper">
                <i className="fa fa-city input-icon dropdown-icon"></i>
                <Select
                  options={cities}
                  value={
                    cities.find(
                      (city) => city.value === formData.dealer_city_id,
                    ) || null
                  }
                  onChange={handleCityChange}
                  placeholder={
                    !formData.dealer_state_id
                      ? "Select state first"
                      : loadingCities
                        ? "Loading cities..."
                        : "Select City *"
                  }
                  isLoading={loadingCities}
                  isDisabled={
                    !formData.dealer_state_id ||
                    hide_show === 2 ||
                    loadingCities
                  }
                  isSearchable={true}
                  styles={customStyles}
                  classNamePrefix="react-select"
                  className={`react-select-container ${validationErrors.dealer_city_id ? "error" : ""}`}
                  noOptionsMessage={() => "No cities available"}
                  loadingMessage={() => "Loading cities..."}
                  aria-label="Select City"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              {validationErrors.dealer_city_id && (
                <small className="error-text">
                  {validationErrors.dealer_city_id}
                </small>
              )}
            </div>

            {/* Sub-City Dropdown */}
            <div className="input-group dropdown-container">
              <div className="input-wrapper dropdown-wrapper">
                <i className="fa fa-map-pin input-icon dropdown-icon"></i>
                <Select
                  options={subCities}
                  value={
                    subCities.find(
                      (subCity) =>
                        subCity.value === formData.dealer_sub_city_id,
                    ) || null
                  }
                  onChange={handleSubCityChange}
                  placeholder={
                    !formData.dealer_city_id
                      ? "Select city first"
                      : loadingSubCities
                        ? "Loading sub-cities..."
                        : "Select Sub-City *"
                  }
                  isLoading={loadingSubCities}
                  isDisabled={
                    !formData.dealer_city_id ||
                    hide_show === 2 ||
                    loadingSubCities
                  }
                  isSearchable={true}
                  styles={customStyles}
                  classNamePrefix="react-select"
                  className={`react-select-container ${validationErrors.dealer_sub_city_id ? "error" : ""}`}
                  noOptionsMessage={() => "No sub-cities available"}
                  loadingMessage={() => "Loading sub-cities..."}
                  aria-label="Select Sub-City"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              {validationErrors.dealer_sub_city_id && (
                <small className="error-text">
                  {validationErrors.dealer_sub_city_id}
                </small>
              )}
            </div>

            {/* OTP Field */}
            {hide_show === 2 && (
              <>
                <div className="input-group">
                  <div className="input-wrapper">
                    <i className="fa fa-shield-alt input-icon"></i>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otp}
                      onChange={(e) => {
                        const val = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 4);
                        setOtp(val);
                      }}
                      placeholder=" "
                      className="input-field"
                      id="otp"
                      maxLength={4}
                    />
                    <label htmlFor="otp" className="input-label">
                      Enter OTP *
                    </label>
                  </div>
                  {otp.length > 0 && otp.length !== 4 && (
                    <small className="error-text">Enter 4-digit OTP</small>
                  )}
                </div>

                {/* Resend OTP Link */}
                <div
                  className="resend-otp"
                  style={{
                    textAlign: "right",
                    marginTop: "-8px",
                    marginBottom: "8px",
                  }}
                >
                  <button
                    type="button"
                    onClick={resendOTP}
                    className="resend-btn"
                    disabled={isLoading}
                    style={{
                      background: "none",
                      border: "none",
                      color: accentColor,
                      fontSize: "14px",
                      cursor: "pointer",
                      padding: "0",
                      textDecoration: "underline",
                      fontWeight: "500",
                    }}
                  >
                    {isLoading ? "Resending..." : "Resend OTP"}
                  </button>
                </div>
              </>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-btn"
              disabled={
                isLoading ||
                // Check only required fields for step 1
                (hide_show === 1 &&
                  (!formData.dealer_mobile ||
                    formData.dealer_mobile.length !== 10 ||
                    !formData.dealer_firm_name.trim() ||
                    !formData.dealer_state_id)) ||
                (hide_show === 2 && (!otp || otp.length !== 4))
              }
            >
              {isLoading ? (
                <>
                  <span>
                    {hide_show === 2 ? "Verifying..." : "Sending OTP..."}
                  </span>
                  <i className="fa fa-spinner fa-spin"></i>
                </>
              ) : (
                <>
                  <span>
                    {hide_show === 2 ? "Verify & Register" : "Send OTP"}
                  </span>
                  <i
                    className={`fa ${
                      hide_show === 2 ? "fa-check" : "fa-paper-plane"
                    }`}
                  ></i>
                </>
              )}
            </button>
          </form>

          <div className="footer-text">
            <p>
              Already have an account?{" "}
              <Link
                to="/login"
                style={{
                  color: accentColor,
                  textDecoration: "underline",
                  fontWeight: "500",
                }}
              >
                Sign In
              </Link>
            </p>

            <p style={{ fontSize: "11px", marginTop: "5px", color: textSecondary }}>
              By registering, you agree to our{" "}
              <span className="link">Terms</span> &{" "}
              <span className="link">Privacy Policy</span>
            </p>

            <Link
              to="/"
              style={{ float: "right", textAlign: "right", marginTop: "10px" }}
            >
              <p style={{ color: accentColor, fontWeight: "500" }}>
                Skip Registration
              </p>
            </Link>
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      <style jsx>{`
        .login-container {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow: hidden;
          font-family: "Segoe UI", sans-serif;
          background: ${backgroundColor};
        }

        .login-card {
          position: relative;
          z-index: 3;
          width: 100%;
          max-width: 440px;
          background: #f4f1f1;
          border-radius: 20px;
          padding: 32px 28px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid ${borderColor};
          animation: slideUp 0.6s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .logo-section {
          text-align: center;
          margin-bottom: 28px;
        }

        .logo-circle {
          width: 70px;
          height: 70px;
          background: ${accentColor};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: white;
          font-size: 28px;
          box-shadow: 0 4px 15px rgba(219, 68, 55, 0.3);
        }

        .logo-section h2 {
          font-size: 24px;
          font-weight: 700;
          color: ${textColor};
          margin: 0 0 6px;
        }

        .logo-section p {
          color: ${textSecondary};
          font-size: 14px;
          margin: 0;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
          position: relative;
          z-index: 1;
        }

        .input-group {
          position: relative;
        }

        .dropdown-container {
          position: relative;
          z-index: 999;
        }

        .input-wrapper {
          position: relative;
          width: 100%;
        }

        .dropdown-wrapper {
          position: relative;
          z-index: 999 !important;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: ${textSecondary};
          font-size: 18px;
          z-index: 1000 !important;
          transition: color 0.3s;
        }

        .dropdown-icon {
          z-index: 1001 !important;
        }

        .input-field {
          width: 100%;
          padding: 16px 16px 16px 48px;
          font-size: 16px;
          background: ${backgroundColor};
          border: 1px solid ${borderColor};
          border-radius: 12px;
          color: ${textColor};
          transition: all 0.3s ease;
          outline: none;
          font-weight: 400;
          position: relative;
          z-index: 1;
        }

        .input-field.error {
          border-color: ${accentColor};
        }

        .input-field::placeholder {
          color: transparent;
        }

        .input-field:focus {
          background: ${backgroundColor};
          border-color: ${accentColor};
          box-shadow: 0 0 0 3px rgba(219, 68, 55, 0.2);
        }

        .input-field:focus + .input-label,
        .input-field:not(:placeholder-shown) + .input-label {
          top: -8px;
          left: 12px;
          font-size: 12px;
          border-radius: 10px;
          background-color: ${accentColor};
          color: #ffffff;
          padding: 0 6px;
          font-weight: 600;
        }

        .input-label {
          position: absolute;
          left: 48px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 15px;
          color: ${textSecondary};
          pointer-events: none;
          transition: all 0.3s ease;
          background: transparent;
          z-index: 1000 !important;
          font-weight: 400;
        }

        .error-text {
          color: ${accentColor};
          font-size: 12px;
          margin-top: 6px;
          padding-left: 48px;
          display: block;
          font-weight: 400;
          position: relative;
          z-index: 1;
        }

        .submit-btn {
          margin-top: 8px;
          padding: 16px;
          font-size: 16px;
          font-weight: 600;
          color: white;
          background: ${accentColor};
          border: none;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(219, 68, 55, 0.3);
          position: relative;
          z-index: 1;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(219, 68, 55, 0.4);
          background: #d63224;
        }

        .submit-btn:active {
          transform: translateY(0);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .submit-btn i {
          font-size: 18px;
          transition: transform 0.3s;
        }

        .submit-btn:hover:not(:disabled) i {
          transform: translateX(4px);
        }

        .footer-text {
          text-align: center;
          margin-top: 25px;
          position: relative;
          z-index: 1;
        }

        .footer-text p {
          font-size: 13px;
          color: ${textColor};
          margin: 5px 0;
          font-weight: 400;
        }

        .link {
          color: ${accentColor};
          font-weight: 500;
          text-decoration: underline;
          cursor: pointer;
        }

        .link:hover {
          color: #d63224;
        }

        /* React Select overrides with high z-index */
        :global(.react-select__control) {
          min-height: 50px !important;
          font-size: 16px !important;
          position: relative !important;
          z-index: 999 !important;
        }

        :global(.react-select-container) {
          position: relative !important;
          z-index: 999 !important;
        }

        :global(.react-select-container.error .react-select__control) {
          border-color: ${accentColor} !important;
        }

        :global(.react-select__value-container) {
          padding: 2px 12px !important;
          z-index: 999 !important;
          position: relative !important;
        }

        :global(.react-select__placeholder) {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          z-index: 999 !important;
        }

        :global(.react-select__menu) {
          z-index: 99999 !important;
          position: fixed !important;
        }

        :global(.react-select__menu-list) {
          z-index: 99999 !important;
          position: relative !important;
        }

        :global(.react-select__option) {
          z-index: 99999 !important;
          position: relative !important;
        }

        :global(.react-select__input-container) {
          z-index: 999 !important;
          position: relative !important;
        }

        :global(.react-select__indicators) {
          z-index: 999 !important;
          position: relative !important;
        }

        /* Mobile Adjustments */
        @media (max-width: 480px) {
          .login-card {
            padding: 24px 20px;
            max-width: 380px;
          }

          .logo-circle {
            width: 60px;
            height: 60px;
            font-size: 24px;
          }

          .logo-section h2 {
            font-size: 22px;
          }

          .input-field {
            padding: 14px 14px 14px 44px;
            font-size: 15px;
          }

          .submit-btn {
            padding: 14px;
            font-size: 15px;
          }

          :global(.react-select__control) {
            min-height: 46px !important;
            font-size: 15px !important;
          }

          :global(.react-select__placeholder) {
            font-size: 14px !important;
          }

          :global(.react-select__single-value) {
            font-size: 15px !important;
          }

          .input-icon {
            left: 12px;
            font-size: 16px;
          }
        }

        @media (max-width: 360px) {
          .login-card {
            max-width: 340px;
            padding: 20px 16px;
          }

          .login-form {
            gap: 16px;
          }
        }
      `}</style>

      {/* Add global styles for react-select portal */}
      <style jsx global>{`
        /* Ensure react-select dropdowns appear above everything */
        .react-select__menu {
          z-index: 99999 !important;
        }

        .react-select__menu-list {
          z-index: 99999 !important;
        }

        .react-select__option {
          z-index: 99999 !important;
        }

        /* Fix for portal positioning */
        body > div:last-child {
          z-index: 99999 !important;
        }
      `}</style>
    </>
  );
}

export default Registration;